import {getTestProps} from '@benchling/js-test-utils';
import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';
import _ from 'underscore';

import {FeatureLibraryAttributes} from 'molbio/feature-libraries/FeatureLibrary';
import {Feature, MatchType} from 'molbio/feature-libraries/FeatureLibraryConstants';
import PopoverColorPicker from 'molbio/sequence-features/PopoverColorPicker';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import {FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';
import EditableTextField from 'ui-library/inputs/EditableTextField';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import ScrollableTable from 'ui-library/scrollable/ScrollableTable';
import {RowSelectionMode, SortDirection} from 'ui-library/scrollable/ScrollableTableConstants';
import {cycleSortDirection} from 'ui-library/scrollable/ScrollableTableUtil';
import sortedBy from 'util/sortedBy';

export const MATCH_TYPE_TO_DISPLAY: Record<MatchType, string> = {
  [MatchType.NUCLEOTIDE]: 'Nucleotide',
  [MatchType.PROTEIN]: 'AA sequence',
};

export interface FeatureTableProps {
  features: Array<Feature>;
  onUpdateFeature: (feature: Feature, field: keyof Feature, value: Feature[keyof Feature]) => void;
  libraries: Array<FeatureLibraryAttributes>;
  onMoveToLibrary: (libraryId: string, features: Array<Feature>) => void;
  onDeleteFeatures: (features: Array<Feature>) => void;
  isReadOnly: boolean;
  currentLibraryId: string;
}

interface TextCellProps {
  text: string;
  onEdit: ((newText: string) => void) | null;
  elementName: string;
}

const TextCell = ({text, onEdit, elementName}: TextCellProps): JSX.Element => (
  <EditableTextField
    isDisabled={onEdit == null}
    value={text}
    requestChange={onEdit || noop}
    dataTestAttrs={{
      component: 'FeatureLibraryItemRow',
      element: elementName,
      key: text,
    }}
  />
);

type SortableAccessor = 'name' | 'feature_type';

export default function FeatureTable({
  features,
  onUpdateFeature,
  libraries,
  currentLibraryId,
  onMoveToLibrary,
  onDeleteFeatures,
  isReadOnly,
}: FeatureTableProps): JSX.Element {
  const getRowContentHeight = (): number => 50;
  const [selectedFeatureIds, setSelectedFeatureIds] = useState<Set<number>>(new Set());
  const [sortKey, setSortKey] = useState<SortableAccessor | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.ASCENDING_SORT);

  const columns = [
    {
      header: '',
      accessorKey: 'color',
      flexGrow: 1,
      isCollapsible: false,
      isSortable: false,
      width: 100,
    },
    {
      header: 'Name',
      accessorKey: 'name',
      flexGrow: 5,
      isCollapsible: false,
      isSortable: true,
      sortKey: 'name',
      sortDirection: sortKey === 'name' ? sortDirection : null,
      width: 150,
    },
    {
      header: '',
      accessorKey: 'matchType',
      flexGrow: 4,
      isCollapsible: false,
      isSortable: false,
      width: 100,
    },
    {
      header: 'Type',
      accessorKey: 'featureType',
      flexGrow: 5,
      isCollapsible: false,
      isSortable: true,
      sortKey: 'feature_type',
      sortDirection: sortKey === 'feature_type' ? sortDirection : null,
      width: 150,
    },
    {
      header: 'Bases',
      accessorKey: 'bases',
      flexGrow: 6,
      isCollapsible: false,
      isSortable: false,
      width: 200,
    },
  ];
  return (
    <div {...getTestProps({component: 'FeatureTable', element: 'root'})}>
      <div className={css(styles.actionButtons)}>
        <Dropdown
          menu={
            <Menu width={MenuWidth.SMALL}>
              {libraries.map((library) => (
                <Option
                  key={library.api_identifier}
                  value={library.api_identifier}
                  onSelect={() => {
                    const selectedFeatures = features.filter((feature) => selectedFeatureIds.has(feature.id));
                    setSelectedFeatureIds(new Set());
                    onMoveToLibrary(library.api_identifier, selectedFeatures);
                  }}
                  isDisabled={library.api_identifier === currentLibraryId}
                  dataTestAttrs={{
                    component: 'FeatureLibraryActionMenu',
                    element: 'targetLibraryOption',
                    key: library.name,
                  }}
                >
                  {library.name}
                </Option>
              ))}
            </Menu>
          }
        >
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            size={ButtonSize.SMALL}
            text="Move to"
            isDropdown={true}
            disabled={isReadOnly || selectedFeatureIds.size === 0}
            dataTestAttrs={{
              component: 'FeatureLibraryActionMenu',
              element: 'moveButton',
            }}
          />
        </Dropdown>
        <Button
          buttonStyle={ButtonStyle.NEGATIVE}
          className={css(styles.deleteButton)}
          size={ButtonSize.SMALL}
          text="Delete"
          onClick={() => {
            const selectedFeatures = features.filter((feature) => selectedFeatureIds.has(feature.id));
            setSelectedFeatureIds(new Set());
            onDeleteFeatures(selectedFeatures);
          }}
          disabled={isReadOnly || selectedFeatureIds.size === 0}
          dataTestAttrs={{
            component: 'FeatureLibraryActionMenu',
            element: 'deleteButton',
          }}
        />
      </div>
      <ScrollableTable
        disableSelection={isReadOnly}
        rowSelectionMode={RowSelectionMode.MULTIPLE}
        onChangeSort={(newSortKey: SortableAccessor) => {
          if (sortKey === newSortKey) {
            setSortDirection(cycleSortDirection(sortDirection));
          } else {
            setSortDirection(SortDirection.ASCENDING_SORT);
            setSortKey(newSortKey);
          }
        }}
        onRowsSelect={(rows, isSelected) => {
          const selectedIds = Array.from(selectedFeatureIds);
          const rowIds = rows.map((row) => row.data.featureId);
          const newIds = isSelected ? _.union(selectedIds, rowIds) : _.difference(selectedIds, rowIds);
          setSelectedFeatureIds(new Set(newIds));
        }}
        columns={columns}
        emptyTableText="This feature library does not contain any features yet."
        getRowContentHeight={getRowContentHeight}
        isCompact={true}
        rows={sortedBy(
          features,
          _.compact([
            sortKey && {accessor: sortKey, reversed: sortDirection === SortDirection.DESCENDING_SORT},
          ])
        ).map((feature, featureIndex) => ({
          id: feature.id.toString(),
          isSelected: selectedFeatureIds.has(feature.id),
          data: {
            featureId: feature.id,
            color: (
              <PopoverColorPicker
                className={css(styles.popover)}
                selectedColor={feature.color}
                onSelectColor={(newColor) => {
                  onUpdateFeature(feature, 'color', newColor);
                }}
                floatingInteraction={FloatingInteraction.CLICK}
                asDropdown={false}
                disabled={isReadOnly}
              />
            ),
            name: (
              <TextCell
                text={feature.name}
                onEdit={
                  isReadOnly
                    ? null
                    : (newName) => {
                        onUpdateFeature(feature, 'name', newName);
                      }
                }
                elementName="nameInput"
              />
            ),
            matchType: (
              <TextCell
                text={MATCH_TYPE_TO_DISPLAY[feature.match_type]}
                onEdit={null}
                elementName="matchTypeInput"
              />
            ),
            featureType: (
              <TextCell
                text={feature.feature_type}
                onEdit={
                  isReadOnly
                    ? null
                    : (newFeatureType) => {
                        onUpdateFeature(feature, 'feature_type', newFeatureType);
                      }
                }
                elementName="featureTypeInput"
              />
            ),
            bases: (
              <TextCell
                text={feature.bases}
                onEdit={
                  isReadOnly
                    ? null
                    : (newBases) => {
                        onUpdateFeature(feature, 'bases', newBases);
                      }
                }
                elementName="residuesInput"
              />
            ),
          },
        }))}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  actionButtons: {
    display: 'flex',
  },
  deleteButton: {marginLeft: '8px'},
  popover: {width: 'fit-content'},
});
